// ─────────────────────────────────────────────────────────────────────────────
// ONE "Return / exchange" composer (Fernando, 2026-09-18: "everything should be fixed").
// Used by Accept Returns (after the scan) and by the order panel. Three steps on one card:
//   1 · what came back            (qty per line; already-returned pieces greyed)
//   2 · what each piece becomes   (Refund / Exchange for… / Nothing) — sizes of the same
//                                  article from /product-family, another product via search
//   3 · how it ships + the money  (send mode, reason; live preview from POST /preview:
//                                  the fee breakdown and paid / refunded / kept)
// Confirm = ONE call, POST /settle, one transaction on the server.
// The line data comes from GET /api/returns/lookup/{key} in both places, so the component
// never has two shapes to understand.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { authFetch, API } from './api';
import { fmtWh } from './time';
import { titleEn } from './lineName';

const R = `${API}/returns`;

export interface LookupLine {
  variation_id: string; article_number?: string; product_name?: string; picklist?: string; wgr_picklist?: string;
  product_name_en?: string;       // English (backend/product_names.py, 26.10) — product_name stays the customer's
  ordered_qty: number; remaining?: number; already_returned?: number;
  unit_paid?: number; gross_unit?: number; discount_share?: number; discount_code?: string; wgr_matched?: boolean;
  /** the shop's own line flags (WGR): non-refundable defaults the piece to "Nothing" */
  not_refundable?: boolean; waiting_for_nos?: boolean;
  /** Ordered vs shipped: garments packing booked on this line that are NOT this variant.
   *  What comes back is the shipped one; the server stores it as such (sold as this line).
   *  source 'packed_items' | 'scan_events' = the pack scan; 'cs' = CS said so on an open exchange. */
  shipped?: { variation_id: string; article_number?: string; product_name?: string; product_name_en?: string; ean?: string; barcode?: string;
              qty: number; at?: string | null; by?: string | null; source?: string }[];
  /** open exchanges still waiting for this line's piece (2026-10-21, Nora #1481): that many pieces
   *  settle the exchange and are NOT refunded, whatever they are marked — the server decides */
  exchange_cover?: { id: number; label: string; qty: number; reason?: string; hold?: boolean; dispatched_at?: string | null }[];
}
/** The garment that comes back for a line: the shipped one when the WHOLE line left as another
 *  garment (the server's rule too), else the line itself. */
export function backOf(l: LookupLine): { variation_id: string; article_number?: string; product_name?: string; product_name_en?: string; wrong: boolean; s?: any } {
  const shipped = l.shipped || [];
  const sum = shipped.reduce((a, s) => a + (s.qty || 0), 0);
  if (shipped.length > 0 && sum >= (l.ordered_qty || 0)) {
    const s = shipped[0];
    return { variation_id: s.variation_id, article_number: s.article_number, product_name: s.product_name, product_name_en: s.product_name_en, wrong: true, s };
  }
  return { variation_id: l.variation_id, article_number: l.article_number, product_name: l.product_name, product_name_en: l.product_name_en, wrong: false };
}
export interface LookupOrder {
  order_id: string; order_number: string | number; customer_name?: string; store_name?: string; webshop_id?: string;
  currency?: string; lines: LookupLine[]; prior_acceptances?: any[]; accept_blocked_reason?: string | null;
  resend_warning?: string | null;
  /** "the customer keeps it" promises on this purchase (2026-10-03): such a piece is not refunded */
  kept_warning?: string | null;
  /** open exchanges waiting for a piece (2026-10-21): such a piece settles the exchange, no refund */
  exchange_warning?: string | null;
}
type Action = 'refund' | 'exchange' | 'none';

const STAGE: Record<string, string> = {
  accepted: 'Accepted', in_return_order: 'In return order', polishing: 'Polishing',
  polished: 'Polished', replenished: 'Replenished', archived: 'Archived',
  held: 'Held off sale', written_off: 'Written off',
};

export function ReturnComposer({ order, arrivedDefault = true, defaultQty = 'zero', onDone, onCancel, compact }: {
  order: LookupOrder;
  /** Accept page: the parcel is in hand. Order panel: the customer announced it. */
  arrivedDefault?: boolean;
  defaultQty?: 'zero' | 'full';
  onDone: (result: any, summary: string) => void;
  onCancel?: () => void;
  compact?: boolean;
}) {
  const money = (v: any) => `${Number(v ?? 0).toFixed(2)} ${(order.currency || 'EUR').toUpperCase() === 'EUR' ? '€' : (order.currency || '').toUpperCase()}`;
  const openedAt = useRef(Date.now());
  const [qty, setQty] = useState<Record<string, number>>(() => {
    const q: Record<string, number> = {};
    (order.lines || []).forEach(l => { q[l.variation_id] = defaultQty === 'full' ? (l.remaining ?? l.ordered_qty ?? 0) : 0; });
    return q;
  });
  const [action, setAction] = useState<Record<string, Action>>({});
  const [wanted, setWanted] = useState<any[]>([]);
  const [familyFor, setFamilyFor] = useState<string | null>(null);      // line whose size picker is open
  const [family, setFamily] = useState<any[]>([]);
  // "came back as a different size": CS says which garment returns for a line when the WMS
  // did not see it at packing — sent as back_variation_id, stored as shipped.source = 'cs'
  const [backFor, setBackFor] = useState<string | null>(null);          // line whose "came back as…" picker is open
  const [backOverride, setBackOverride] = useState<Record<string, any>>({});   // line vid → chosen variant
  const [backFamily, setBackFamily] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [noneReason, setNoneReason] = useState('');
  const [kind, setKind] = useState<'return' | 'unclaimed_cod'>('return');
  const [arrived, setArrived] = useState(arrivedDefault);
  const [sendMode, setSendMode] = useState<'immediate' | 'on_return'>('immediate');
  const [noReturn, setNoReturn] = useState(false);
  const [reasons, setReasons] = useState<any[]>([]);
  const [reasonId, setReasonId] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [preview, setPreview] = useState<any | null>(null);
  const [previewErr, setPreviewErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const marked = (order.lines || []).filter(l => (qty[l.variation_id] || 0) > 0);
  const totalMarked = marked.reduce((a, l) => a + (qty[l.variation_id] || 0), 0);
  // a line the shop marked non-refundable defaults to "Nothing" (CS can still choose otherwise)
  const nonRefundable = new Set((order.lines || []).filter(l => l.not_refundable).map(l => l.variation_id));
  const actionOf = (vid: string): Action => action[vid] || (nonRefundable.has(vid) ? 'none' : 'refund');
  const exchangeLines = marked.filter(l => actionOf(l.variation_id) === 'exchange');
  const noneLines = marked.filter(l => actionOf(l.variation_id) === 'none');
  // "where returns are accepted" (accept_in_shop_platforms) blocks ACCEPTING the goods here — never
  // registering an exchange while they are not here (Fernando, 3.10: SK4243's "+" did nothing since 18.9)
  const blocked = !!order.accept_blocked_reason;
  const acceptBlocked = blocked && arrived;

  const body = useMemo(() => ({
    order_id: order.order_id, order_number: String(order.order_number),
    items: marked.map(l => ({ variation_id: l.variation_id, qty: qty[l.variation_id] || 0, action: kind === 'unclaimed_cod' ? 'refund' : actionOf(l.variation_id),
                              back_variation_id: backOverride[l.variation_id]?.variation_id || null })),
    wanted_items: kind === 'unclaimed_cod' ? [] : wanted.map(w => ({ variation_id: w.variation_id, qty: w.qty })),
    kind, arrived, send_mode: noReturn ? 'immediate' : sendMode, no_return: noReturn,
    reason_id: reasonId ? Number(reasonId) : null, reason_text: reasonId ? null : (reasonText.trim() || null),
    none_reason: noneReason.trim() || null,
    accept_seconds: Math.round((Date.now() - openedAt.current) / 1000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [order.order_id, JSON.stringify(qty), JSON.stringify(action), JSON.stringify(wanted), JSON.stringify(backOverride), kind, arrived, sendMode, noReturn, reasonId, reasonText, noneReason]);

  const ready = totalMarked > 0 && !acceptBlocked
    && (kind === 'unclaimed_cod' || exchangeLines.length === 0 || wanted.length > 0)
    && (noneLines.length === 0 || noneReason.trim().length > 0)
    && (arrived || exchangeLines.length > 0);

  useEffect(() => {
    if (exchangeLines.length > 0) authFetch(`${R}/reasons?kind=exchange&active_only=true`).then(r => r.json()).then(d => setReasons(d.reasons || [])).catch(() => {});
  }, [exchangeLines.length > 0]);   // eslint-disable-line react-hooks/exhaustive-deps

  // the size picker for the line whose "Exchange for…" is open
  useEffect(() => {
    if (!familyFor) { setFamily([]); return; }
    const store = order.webshop_id ? `&webshop_id=${encodeURIComponent(order.webshop_id)}` : '';
    authFetch(`${R}/product-family?variation_id=${encodeURIComponent(familyFor)}${store}`)
      .then(r => r.ok ? r.json() : { results: [] }).then(d => setFamily(d.results || [])).catch(() => setFamily([]));
  }, [familyFor, order.webshop_id]);

  // the size picker for the line whose "came back as…" is open — the same family list
  useEffect(() => {
    if (!backFor) { setBackFamily([]); return; }
    const store = order.webshop_id ? `&webshop_id=${encodeURIComponent(order.webshop_id)}` : '';
    authFetch(`${R}/product-family?variation_id=${encodeURIComponent(backFor)}${store}`)
      .then(r => r.ok ? r.json() : { results: [] }).then(d => setBackFamily(d.results || [])).catch(() => setBackFamily([]));
  }, [backFor, order.webshop_id]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (search.trim().length < 2) { setResults([]); return; }
      const store = order.webshop_id ? `&webshop_id=${encodeURIComponent(order.webshop_id)}` : '';
      const res = await authFetch(`${R}/product-search?q=${encodeURIComponent(search.trim())}${store}`);
      if (res.ok) setResults((await res.json()).results || []);
    }, 300);
    return () => clearTimeout(t);
  }, [search, order.webshop_id]);

  // live preview: the same numbers the confirm will produce
  useEffect(() => {
    if (!ready) { setPreview(null); return; }
    const t = setTimeout(async () => {
      const res = await authFetch(`${R}/preview`, { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) { setPreview(await res.json()); setPreviewErr(''); }
      else { const e = await res.json().catch(() => ({})); setPreview(null); setPreviewErr(typeof e.detail === 'string' ? e.detail : (e.detail?.message || 'Preview failed')); }
    }, 400);
    return () => clearTimeout(t);
  }, [body, ready]);

  const setLineQty = (l: LookupLine, v: number) => {
    const max = l.remaining ?? l.ordered_qty;
    setQty(p => ({ ...p, [l.variation_id]: Math.max(0, Math.min(max, v)) }));
    // marking a non-refundable line: say why there is no refund, unless CS already typed a reason
    if (v > 0 && l.not_refundable && !noneReason.trim()) setNoneReason('marked non-refundable in the shop');
  };
  const chooseAction = (vid: string, a: Action) => {
    setAction(p => ({ ...p, [vid]: a }));
    if (a === 'exchange') setFamilyFor(vid); else if (familyFor === vid) setFamilyFor(null);
  };
  const addWanted = (r: any, forLine?: LookupLine) => {
    if (wanted.find(w => w.variation_id === r.variation_id)) return;
    setWanted(w => [...w, { ...r, qty: forLine ? (qty[forLine.variation_id] || 1) : 1 }]);
    setSearch(''); setResults([]);
  };
  const setWantQty = (vid: string, q: number) => setWanted(w => w.map(x => x.variation_id === vid ? { ...x, qty: Math.max(1, q) } : x));
  const removeWanted = (vid: string) => setWanted(w => w.filter(x => x.variation_id !== vid));

  const confirm = async () => {
    if (!ready || busy) return;
    const prior: any[] = order.prior_acceptances || [];
    let confirmRepeat = false;
    if (prior.length > 0 && arrived) {
      const when = prior.map((a: any) => `${a.accepted_at ? fmtWh(a.accepted_at, 'datetime') : '?'} by ${a.accepted_by || '?'} — ${(a.items || []).map((i: any) => `${i.qty_returned}× ${i.article_number || titleEn(i)}`).join(', ')}${a.return_order?.code ? ` (${a.return_order.code}, ${STAGE[a.stage] || a.stage})` : ''}`).join('\n');
      if (!window.confirm(`A return for order ${order.order_number} was already accepted ${prior.length === 1 ? 'once' : prior.length + ' times'}:\n\n${when}\n\nAccept the remaining pieces again?`)) return;
      confirmRepeat = true;
    }
    setBusy(true); setMsg('');
    const res = await authFetch(`${R}/settle`, { method: 'POST', body: JSON.stringify({ ...body, confirm_repeat: confirmRepeat }) });
    setBusy(false);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      const det = d.detail;
      setMsg(`⚠ ${typeof det === 'string' ? det : (det?.message || det?.[0]?.msg || 'Could not save')}`);
      return;
    }
    const parts: string[] = [];
    const pieces = (d.accepted || []).reduce((a: number, c: any) => a + (c.pieces || 0), 0);
    if (arrived) parts.push(`accepted ${pieces} pc${pieces === 1 ? '' : 's'}`);
    if (d.refund_raised) parts.push(d.auto_refund_queued ? 'refund queued for Paytrail' : 'refund in Manual refunds');
    if (d.refund_policy === 'resent') parts.push('no refund — the goods were re-sent');
    if (d.kept_note) parts.push(`no refund for the piece the customer was told to keep${(d.accepted || []).some((c: any) => c.kind === 'kept' && !c.exchange_id) ? ' — held off sale for a manager' : ''}`);
    if ((d.settled_by_exchanges || []).length) parts.push(`no refund — settles ${d.settled_by_exchanges.map((i: number) => `E${i}`).join(', ')}${(d.accepted || []).some((c: any) => c.kind === 'kept' && c.exchange_id) ? ' (held off sale for a manager)' : ''}`);
    if (d.exchange) {
      if (d.exchange.cancelled) parts.push(`⚠ exchange could not reserve stock: ${d.exchange.cancelled}`);
      else parts.push(`exchange E${d.exchange.id}${d.exchange.reserved ? ' reserved' : ' — not everything reserved, see Exchange orders'}${d.exchange.pass_started ? ', order back in New for the replacement' : ''}`);
    }
    if (d.family_warning) parts.push(`⚠ ${d.family_warning}`);
    onDone(d, parts.join(' · '));
  };

  const t = preview?.after_return?.totals;
  const lineBox = compact ? 'py-1.5' : 'py-2';

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden text-sm">
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2.5">
        <span className="text-white font-semibold">Return / exchange · order {order.order_number}</span>
        <div className="flex items-center gap-3 text-xs">
          <label className={`flex items-center gap-1 ${blocked && !arrived ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-pointer'}`}
                 title={blocked ? (order.accept_blocked_reason || '') : undefined}>
            {/* a store whose returns are accepted in the shop: can be unticked (register an exchange), never ticked */}
            <input type="checkbox" checked={arrived} disabled={blocked && !arrived} onChange={e => setArrived(e.target.checked)} /> the goods are here
          </label>
          <label className={`flex items-center gap-1 cursor-pointer ${kind === 'unclaimed_cod' ? 'text-amber-300' : 'text-gray-400'}`}>
            <input type="checkbox" checked={kind === 'unclaimed_cod'} onChange={e => setKind(e.target.checked ? 'unclaimed_cod' : 'return')} /> unclaimed COD
          </label>
          {onCancel && <button onClick={onCancel} className="text-gray-400 hover:text-white">Cancel</button>}
        </div>
      </div>
      {acceptBlocked && <div className="mx-4 mt-3 px-3 py-2 rounded border-2 border-red-700 bg-red-950/50 text-red-300 font-semibold">⛔ {order.accept_blocked_reason}
        <div className="mt-1 text-xs font-normal text-red-200/80">To register an exchange for this order instead, untick “the goods are here”.</div></div>}
      {blocked && !arrived && <div className="mx-4 mt-3 px-3 py-2 rounded border border-gray-700 bg-gray-800/50 text-gray-300 text-xs">
        {order.store_name || 'This store'}'s returns are accepted in the shop — here you register the exchange; the old piece is accepted in the shop when it arrives.</div>}
      {(order.prior_acceptances || []).length > 0 && (
        <div className="mx-4 mt-3 px-3 py-2 rounded border border-red-800/70 bg-red-950/30 text-xs">
          <div className="text-red-300 font-semibold mb-1">⚠ Already accepted {order.prior_acceptances!.length === 1 ? 'once' : `${order.prior_acceptances!.length} times`} — greyed pieces cannot come back again</div>
          {order.prior_acceptances!.map((a: any) => (
            <div key={a.id} className="text-gray-300">
              <span className="text-gray-400">{a.accepted_at ? fmtWh(a.accepted_at, 'datetime') : '—'}</span><span className="text-gray-500"> by {a.accepted_by}</span>
              {a.return_order?.code && <span className="text-gray-500"> · {a.return_order.code} · {STAGE[a.stage] || a.stage}</span>}
              <span className="ml-2">{(a.items || []).map((i: any) => `${i.qty_returned}× ${i.article_number || titleEn(i)}`).join(', ')}</span>
            </div>
          ))}
        </div>
      )}
      {order.resend_warning && <div className="mx-4 mt-3 px-3 py-2 rounded border border-amber-800/60 bg-amber-950/40 text-amber-200 text-xs">↻ {order.resend_warning}</div>}
      {order.kept_warning && <div className="mx-4 mt-3 px-3 py-2 rounded border border-teal-800/60 bg-teal-950/40 text-teal-200 text-xs">✋ {order.kept_warning}</div>}
      {order.exchange_warning && <div className="mx-4 mt-3 px-3 py-2 rounded border border-purple-800/60 bg-purple-950/40 text-purple-200 text-xs">⇄ {order.exchange_warning}</div>}

      <div className="p-4 space-y-5">
        {/* 1 · what came back, and what each piece becomes */}
        <div>
          <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">1 · What came back — and what each piece becomes</div>
          <table className="w-full">
            <thead className="text-gray-500 text-xs text-left border-b border-gray-800">
              <tr><th className="py-1.5">Item</th><th className="py-1.5 text-right">Paid / pc</th><th className="py-1.5 md:w-40 text-center">Qty back</th><th className="py-1.5 md:w-64">Becomes</th></tr>
            </thead>
            <tbody>
              {(order.lines || []).map(l => {
                const max = l.remaining ?? l.ordered_qty;
                const q = qty[l.variation_id] || 0;
                const a = actionOf(l.variation_id);
                const locked = acceptBlocked || max <= 0;
                // a disabled button says why — a dead "+" with no reason is what Fernando hit on 3.10
                const whyLocked = max <= 0 ? 'Nothing left to return on this line — it already came back'
                  : acceptBlocked ? (order.accept_blocked_reason || '') : undefined;
                // what comes BACK for this line: CS's choice, else the garment that left
                // (pack scan), else the line itself — the header names it
                const ov = backOverride[l.variation_id];
                const back = ov ? { variation_id: ov.variation_id, article_number: ov.product_code || ov.article_number, product_name: ov.product_name, product_name_en: ov.product_name_en, wrong: true, s: null }
                                : backOf(l);
                const partly = !back.wrong && (l.shipped || []).length > 0;   // only part of the line left as another garment
                return (
                  <React.Fragment key={l.variation_id}>
                    <tr className={`border-b border-gray-800/50 ${max <= 0 ? 'opacity-50' : ''}`}>
                      <td className={lineBox}>
                        {back.wrong ? (
                          <>
                            <div className="font-medium text-gray-200">
                              <span className="text-[10px] uppercase tracking-wider text-red-300 mr-1.5">expect back</span>{back.article_number || titleEn(back)}
                            </div>
                            <div className="text-gray-500 text-xs">{titleEn(back)}
                              <span className="ml-2 text-gray-400">· sold as <span className="text-gray-300">{l.article_number}</span> {titleEn(l)}</span>
                              {l.wgr_matched === false && <span className="ml-2 text-amber-500">· not in WGR</span>}
                              {(l.already_returned || 0) > 0 && <span className="ml-2 text-red-400">· {l.already_returned} of {l.ordered_qty} already returned</span>}
                            </div>
                            <div className="mt-0.5 text-[10px] text-red-200/80"
                                 title="The piece coming back is booked as itself (stock follows the garment) and refunded as the line it was sold as.">
                              {ov ? 'CS: the customer has this garment, not the line ordered'
                                  : back.s?.source === 'cs' ? `CS said so${back.s?.by ? ` (${back.s.by})` : ''}`
                                  : `🚚 left this way — packing scanned ${back.s?.barcode || back.s?.ean || '?'}${back.s?.at ? ` on ${fmtWh(back.s.at, 'short')}` : ''}${back.s?.by ? ` by ${back.s.by}` : ''}`}
                              {ov && <button onClick={() => setBackOverride(o => { const n = { ...o }; delete n[l.variation_id]; return n; })} className="ml-2 underline text-gray-400 hover:text-white">undo</button>}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="font-medium text-gray-200">{l.article_number}
                              {l.not_refundable && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-200 align-middle" title="The shop marked this line non-refundable (WGR isNotRefundable) — it defaults to Nothing here">non-refundable</span>}
                            </div>
                            <div className="text-gray-500 text-xs">{titleEn(l)}{l.wgr_matched === false && <span className="ml-2 text-amber-500">· not in WGR</span>}
                              {(l.already_returned || 0) > 0 && <span className="ml-2 text-red-400">· {l.already_returned} of {l.ordered_qty} already returned</span>}</div>
                            {partly && (l.shipped || []).map((s, k) => (
                              <div key={k} className="mt-1 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-900/60 text-red-200"
                                   title={`Packing scanned barcode ${s.barcode || s.ean || '?'}${s.at ? ` on ${fmtWh(s.at, 'short')}` : ''}${s.by ? ` by ${s.by}` : ''}. Part of this line left as this garment; the desk accepts it by its own barcode.`}>
                                🚚 {s.qty} of {l.ordered_qty} shipped as {s.article_number || titleEn(s)}
                              </div>
                            ))}
                          </>
                        )}
                        {!locked && !ov && (
                          <button onClick={() => setBackFor(backFor === l.variation_id ? null : l.variation_id)}
                                  className="mt-1 block text-[10px] text-gray-500 hover:text-white underline decoration-dotted">
                            {backFor === l.variation_id ? 'close' : back.wrong ? 'comes back as something else…' : 'came back as a different size…'}
                          </button>
                        )}
                        {backFor === l.variation_id && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {backFamily.length === 0 && <span className="text-gray-600 text-[10px]">no other sizes found</span>}
                            {backFamily.filter(f => f.variation_id !== l.variation_id).map(f => (
                              <button key={f.variation_id} onClick={() => { setBackOverride(o => ({ ...o, [l.variation_id]: f })); setBackFor(null); }}
                                title={`${titleEn(f)} · ${f.picklist || f.product_code}`}
                                className="px-2 py-0.5 rounded border border-gray-700 text-[10px] text-gray-300 hover:bg-gray-800">
                                {f.size || f.product_code}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={`${lineBox} text-right text-gray-300 whitespace-nowrap`}>
                        {money(l.unit_paid)}
                        {(l.gross_unit || 0) > (l.unit_paid || 0) + 0.004 && <div className="text-[10px] text-gray-500 line-through">{money(l.gross_unit)}{l.discount_code ? ` ${l.discount_code}` : ''}</div>}
                      </td>
                      <td className={lineBox}>
                        <div className="flex items-center justify-center gap-2">
                          <button disabled={locked} title={whyLocked} onClick={() => setLineQty(l, q - 1)} className="w-7 h-7 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">−</button>
                          <span className="w-8 text-center font-semibold">{q}</span>
                          <button disabled={locked} title={whyLocked} onClick={() => setLineQty(l, q + 1)} className="w-7 h-7 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">+</button>
                          <span className="text-gray-600 text-xs">/ {max}</span>
                        </div>
                      </td>
                      <td className={lineBox}>
                        {q > 0 && kind === 'return' && (
                          <div className="flex flex-wrap gap-1">
                            {(['refund', 'exchange', 'none'] as Action[]).map(x => (
                              <button key={x} onClick={() => chooseAction(l.variation_id, x)}
                                className={`px-2 py-1 rounded text-xs border ${a === x ? (x === 'refund' ? 'bg-green-800 border-green-600 text-white' : x === 'exchange' ? 'bg-purple-800 border-purple-600 text-white' : 'bg-gray-700 border-gray-500 text-white') : 'border-gray-700 text-gray-400 hover:text-white'}`}>
                                {x === 'refund' ? 'Refund' : x === 'exchange' ? 'Exchange for…' : 'Nothing'}
                              </button>
                            ))}
                          </div>
                        )}
                        {q > 0 && kind === 'return' && a !== 'exchange' && (l.exchange_cover || []).length > 0 && (() => {
                          // the server settles these pieces against the exchange whatever they are marked
                          let left = q;
                          const rows = (l.exchange_cover || []).map(c => { const n = Math.min(left, c.qty); left -= n; return { c, n }; }).filter(r => r.n > 0);
                          return (
                            <div className="mt-1 text-[11px] text-purple-200"
                                 title="An open exchange waits for this piece: it settles the exchange — the customer already has (or gets) the replacement — so it is not refunded.">
                              {rows.map(({ c, n }) => (
                                <div key={c.id}>⇄ {n} pc settle{n === 1 ? 's' : ''} {c.label}{c.reason ? ` (${c.reason})` : ''} — no refund
                                  {c.dispatched_at ? `, replacement sent ${fmtWh(c.dispatched_at, 'date')}` : ', replacement not sent yet'}
                                  {c.hold ? ' · held off sale for a manager' : ''}</div>
                              ))}
                              {left > 0 && <div className="text-gray-400">the other {left} pc: as marked</div>}
                            </div>
                          );
                        })()}
                        {q > 0 && kind === 'unclaimed_cod' && <span className="text-xs text-amber-300">counted in, nothing collected</span>}
                      </td>
                    </tr>
                    {q > 0 && a === 'exchange' && familyFor === l.variation_id && (
                      <tr className="bg-[#0f1117]"><td colSpan={4} className="px-3 py-2">
                        <div className="text-xs text-gray-500 mb-1">Sizes of this article — click one to add it as the replacement</div>
                        <div className="flex flex-wrap gap-1.5">
                          {family.length === 0 && <span className="text-gray-600 text-xs">no other sizes found — search another product below</span>}
                          {family.map(f => {
                            // EVERY size can be the replacement — a defect is exchanged for the same
                            // size (Fernando, 2026-09-22). The labels only say what each one is.
                            const coming = String(f.variation_id) === String(back.variation_id);
                            return (
                              <button key={f.variation_id} disabled={!!wanted.find(w => w.variation_id === f.variation_id)} onClick={() => addWanted(f, l)}
                                title={`${titleEn(f)} · ${f.picklist} · stock ${f.stock_qty}`}
                                className={`px-2 py-1 rounded border text-xs ${(f.stock_qty || 0) > 0 ? 'border-green-800 text-green-300 hover:bg-green-900/40' : 'border-amber-900 text-amber-400 hover:bg-amber-900/30'}`}>
                                {f.size || f.product_code}{coming ? ' (the one coming back)' : f.is_current && back.wrong ? ' (the size ordered)' : f.is_current ? ' (same size)' : ''} <span className="text-gray-500">· {f.stock_qty ?? '?'}</span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="…or another product: code, barcode or name"
                            className="flex-1 px-3 py-1.5 rounded bg-[#1a1d27] border border-gray-700 text-xs focus:outline-none focus:border-blue-500" />
                        </div>
                        {results.length > 0 && (
                          <div className="mt-1 border border-gray-800 rounded max-h-40 overflow-auto">
                            {results.map(r => (
                              <div key={r.variation_id} onClick={() => addWanted(r, l)} className="flex items-center gap-2 px-3 py-1 hover:bg-gray-800 cursor-pointer text-xs">
                                <span className="flex-1">{titleEn(r)} <span className="text-gray-500">{r.size || ''}</span></span>
                                <span className="text-gray-500">{r.picklist}</span>
                                <span className={r.stock_qty > 0 ? 'text-green-400' : 'text-amber-500'}>stock {r.stock_qty}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {noneLines.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-400 whitespace-nowrap">Why no refund for {noneLines.length === 1 ? 'this piece' : 'these pieces'}?</span>
              <input value={noneReason} onChange={e => setNoneReason(e.target.value)} placeholder="required — e.g. goodwill, customer keeps a damaged item, paid by voucher"
                className="flex-1 px-3 py-1.5 rounded bg-[#0f1117] border border-gray-700 text-xs focus:outline-none focus:border-blue-500" />
            </div>
          )}
        </div>

        {/* 2 · the replacement */}
        {wanted.length > 0 && (
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">2 · Replacement going out</div>
            <div className="space-y-1">
              {wanted.map(w => {
                const st = (preview?.wanted_stock || []).find((x: any) => x.variation_id === w.variation_id);
                return (
                  <div key={w.variation_id} className="flex items-center gap-2 bg-[#0f1117] border border-gray-800 rounded px-3 py-1.5">
                    <span className="flex-1">{titleEn(w)} <span className="text-gray-500">{w.size || ''}</span>{w.wgr_matched === false && <span className="text-amber-500 text-xs ml-1">· not in WGR</span>}
                      {st && <span className={`text-xs ml-2 ${st.enough ? 'text-green-400' : 'text-amber-400'}`}>stock {st.stock_qty ?? '?'}{st.enough ? '' : ' — not enough'}</span>}</span>
                    <button onClick={() => setWantQty(w.variation_id, w.qty - 1)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700">−</button>
                    <span className="w-6 text-center">{w.qty}</span>
                    <button onClick={() => setWantQty(w.variation_id, w.qty + 1)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700">+</button>
                    <button onClick={() => removeWanted(w.variation_id)} className="text-gray-600 hover:text-red-400">✕</button>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="space-y-1">
                <label className={`flex items-center gap-2 ${noReturn || arrived ? 'opacity-60' : 'cursor-pointer'}`}>
                  <input type="radio" name="rc-send" checked={arrived || noReturn || sendMode === 'immediate'} disabled={noReturn || arrived} onChange={() => setSendMode('immediate')} />
                  <span className="text-gray-300">Send the replacement now</span>
                </label>
                <label className={`flex items-center gap-2 ${noReturn || arrived || blocked ? 'opacity-60' : 'cursor-pointer'}`}
                       title={blocked && !arrived && !noReturn ? (order.accept_blocked_reason || 'Returns of this store are accepted in the shop, so the WMS never sees the return arrive') : undefined}>
                  <input type="radio" name="rc-send" checked={!arrived && !noReturn && sendMode === 'on_return'} disabled={noReturn || arrived || blocked} onChange={() => setSendMode('on_return')} />
                  <span className="text-gray-300">Send when the return arrives{blocked && !arrived && !noReturn ? ' — not here: the return is accepted in the shop' : ''}</span>
                </label>
                {arrived && <div className="text-gray-500">The goods are here, so the replacement goes now.</div>}
                {!arrived && <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={noReturn} onChange={e => { setNoReturn(e.target.checked); if (e.target.checked) setSendMode('immediate'); }} /><span className="text-gray-300">The customer keeps the original — nothing comes back</span></label>}
              </div>
              <div className="flex gap-2">
                <select value={reasonId} onChange={e => setReasonId(e.target.value)} className="px-2 py-1.5 rounded bg-[#0f1117] border border-gray-700 focus:outline-none focus:border-blue-500">
                  <option value="">— exchange reason —</option>
                  {reasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
                {!reasonId && <input value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="…or type a reason" className="flex-1 px-2 py-1.5 rounded bg-[#0f1117] border border-gray-700 focus:outline-none focus:border-blue-500" />}
              </div>
            </div>
          </div>
        )}
        {exchangeLines.length > 0 && wanted.length === 0 && <div className="text-xs text-amber-300">Pick the replacement size or product for the piece marked "Exchange for…".</div>}

        {/* 3 · the money, live */}
        <div>
          <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">3 · What the money does</div>
          {!ready && <div className="text-gray-600 text-xs">Mark what came back to see the refund and the order after return.</div>}
          {previewErr && <div className="text-amber-300 text-xs">{previewErr}</div>}
          {preview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0f1117] border border-gray-800 rounded p-3 space-y-1">
                <div className="text-gray-400 font-semibold mb-1">Refund</div>
                {preview.refund ? (
                  <>
                    {(preview.refund.breakdown || []).map((b: any, i: number) => (
                      <div key={i} className={`flex justify-between ${b.total ? 'text-white font-semibold border-t border-gray-800 pt-1 mt-1' : 'text-gray-300'}`}>
                        <span>{b.label}{b.note ? <span className="text-gray-500"> · {b.note}</span> : null}</span><span>{money(b.amount)}</span>
                      </div>
                    ))}
                    {(preview.refund.flags || []).map((f: string, i: number) => <div key={i} className="text-amber-300">⚠ {f}</div>)}
                    {(preview.refund.over_returned || []).length > 0 && <div className="text-red-400">⚠ already refunded in full: {preview.refund.over_returned.join(', ')}</div>}
                  </>
                ) : <div className="text-gray-500">{preview.refund_policy === 'resent' ? 'No refund — the goods were re-sent to the customer.' : preview.refund_policy === 'kept' ? 'No refund — the customer was told to keep it.' : kind === 'unclaimed_cod' ? 'No refund — nothing was collected.' : 'No refund for the pieces as marked.'}</div>}
                {preview.resend_note && <div className="text-amber-300 mt-1">↻ {preview.resend_note}</div>}
                {preview.kept_note && <div className="text-teal-300 mt-1">✋ {preview.kept_note}</div>}
              </div>
              <div className="bg-[#0f1117] border border-gray-800 rounded p-3 space-y-1">
                <div className="text-gray-400 font-semibold mb-1">Order after this</div>
                {t && (
                  <>
                    <div className="flex justify-between text-gray-300"><span>Customer paid</span><span>{money(t.paid)}</span></div>
                    <div className="flex justify-between text-gray-300"><span>Refunded so far</span><span>−{money(t.refunded)}</span></div>
                    {t.refund_pending > 0 && <div className="flex justify-between text-amber-300"><span>This refund (pending)</span><span>−{money(t.refund_pending)}</span></div>}
                    <div className="flex justify-between text-white font-semibold border-t border-gray-800 pt-1 mt-1"><span>Kept after this</span><span>{money(Number(t.kept) - Number(t.refund_pending || 0))}</span></div>
                    <div className="flex justify-between text-gray-400"><span>Order after return is worth</span><span>{money(t.total_after)}</span></div>
                    {preview.after_return.why_not_reconciled && <div className="text-gray-500">{preview.after_return.why_not_reconciled}</div>}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {msg && <div className={`text-sm ${msg.startsWith('⚠') ? 'text-amber-300' : 'text-gray-200'}`}>{msg}</div>}
        <div className="flex items-center justify-end gap-3">
          <span className="text-xs text-gray-500">{totalMarked} pc{totalMarked === 1 ? '' : 's'}{exchangeLines.length ? ` · ${exchangeLines.length} exchange` : ''}{noneLines.length ? ` · ${noneLines.length} no refund` : ''}</span>
          <button onClick={confirm} disabled={!ready || busy}
            className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-white">
            {busy ? 'Saving…' : arrived ? `Accept${exchangeLines.length ? ' & exchange' : ''}` : 'Register exchange'}
          </button>
        </div>
      </div>
    </div>
  );
}
